import { Alert, Button, FormField, Input, Module, ModuleBody, ModuleHead, Select } from "@vjal/ui";
import { useState } from "react";
import { destructiveButtonClass } from "../components/ui-classes";
import { evaluateActionCapability } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { useCoachingZ6Mutations } from "../hooks/useCoachingZ6Mutations";
import { asLiveApiId, useLiveCoachingList } from "../hooks/useLiveProductData";

/**
 * Z6 admin actions for the cohort overview:
 * - F13 reassign cohort coach (`sessions:reassign-coach`, admin-only)
 * - F6 remove participant (`participants:update`, operational write roles)
 * Capabilities are evaluated independently so coach can remove participants
 * without gaining coach-reassignment, and presentation is not coupled.
 */
export function CohortAdminActionsPanel({ cohortId, role }: { cohortId: string | null; role: CoachingRole }) {
  const reassignDecision = evaluateActionCapability(role, "sessions:reassign-coach");
  const removeDecision = evaluateActionCapability(role, "participants:update");
  // Only surface controls the role may operate (enabled) or that are blocked as
  // unavailable for an otherwise allowed operator. Read-only/disabled and fully
  // hidden presentations do not mount destructive controls or live-list reads.
  const showReassign = reassignDecision.presentation === "enabled" || reassignDecision.presentation === "disabled-unavailable";
  const showRemove = removeDecision.presentation === "enabled" || removeDecision.presentation === "disabled-unavailable";
  if (!showReassign && !showRemove) return null;

  return (
    <Module span={12}>
      <ModuleHead
        title="Admin cohort actions"
        subtitle="Reassign the cohort coach or remove a participant. Past sessions and recordings are preserved."
      />
      <ModuleBody className="grid gap-5 lg:grid-cols-2">
        {showReassign ? (
          <CohortReassignControl
            cohortId={cohortId}
            decisionAllowed={reassignDecision.allowed}
            reason={reassignDecision.reason}
          />
        ) : null}
        {showRemove ? (
          <ParticipantRemoveControl
            cohortId={cohortId}
            decisionAllowed={removeDecision.allowed}
            reason={removeDecision.reason}
          />
        ) : null}
      </ModuleBody>
    </Module>
  );
}

function CohortReassignControl({
  cohortId,
  decisionAllowed,
  reason,
}: {
  cohortId: string | null;
  decisionAllowed: boolean;
  reason: string;
}) {
  const users = useLiveCoachingList("users.list");
  const { reassignCohortCoach } = useCoachingZ6Mutations();
  const [newCoachId, setNewCoachId] = useState("");
  const [notes, setNotes] = useState("");
  const disabled = !cohortId || !decisionAllowed || !newCoachId;

  return (
    <div className="space-y-3">
      <p className="m-0 text-sm font-semibold text-strong">Reassign cohort coach (F13)</p>
      {!decisionAllowed ? <Alert variant="info">{reason}</Alert> : null}
      <Select
        label="New coach"
        value={newCoachId}
        disabled={!decisionAllowed}
        onChange={(event) => setNewCoachId(event.target.value)}
      >
        <option value="">Select a coach…</option>
        {users.items.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name || user.email || `User ${user.id}`}
          </option>
        ))}
      </Select>
      <FormField label="Notes (optional)">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Reason for reassignment"
          />
        )}
      </FormField>
      <Button
        type="button"
        disabled={disabled}
        loading={reassignCohortCoach.isPending}
        onClick={() =>
          cohortId &&
          newCoachId &&
          reassignCohortCoach.mutate({
            cohortId,
            newCoachId: Number(asLiveApiId(newCoachId)),
            notes: notes.trim() || undefined,
          })
        }
      >
        Reassign cohort
      </Button>
      {reassignCohortCoach.isSuccess ? (
        <Alert variant="info">
          Cohort reassignment recorded. Future sessions are re-hosted under the new coach (fresh join URLs); past sessions and recordings are unchanged. Provider fan-out runs when the Zoom gate is open.
        </Alert>
      ) : null}
    </div>
  );
}

function ParticipantRemoveControl({
  cohortId,
  decisionAllowed,
  reason,
}: {
  cohortId: string | null;
  decisionAllowed: boolean;
  reason: string;
}) {
  const participants = useLiveCoachingList("participants.list", {
    query: cohortId ? { cohort_id: cohortId } : undefined,
  });
  const { removeParticipant } = useCoachingZ6Mutations();
  const [participantId, setParticipantId] = useState("");
  const disabled = !cohortId || !decisionAllowed || !participantId;

  return (
    <div className="space-y-3">
      <p className="m-0 text-sm font-semibold text-strong">Remove participant (F6)</p>
      {!decisionAllowed ? <Alert variant="info">{reason}</Alert> : null}
      <Select
        label="Participant"
        value={participantId}
        disabled={!decisionAllowed}
        onChange={(event) => setParticipantId(event.target.value)}
      >
        <option value="">Select a participant…</option>
        {participants.items.map((participant) => (
          <option key={participant.id} value={participant.id}>
            {participant.name || participant.email || `Participant ${participant.id}`}
          </option>
        ))}
      </Select>
      <Button
        type="button"
        variant="danger"
        className={destructiveButtonClass}
        disabled={disabled}
        loading={removeParticipant.isPending}
        onClick={() =>
          cohortId && participantId && removeParticipant.mutate({ cohortId, participantId })
        }
      >
        Remove from cohort
      </Button>
      {removeParticipant.isSuccess ? (
        <Alert variant="info">
          Participant removed. Future Zoom registrants and calendar invites are cancelled when the provider gates are open.
        </Alert>
      ) : null}
    </div>
  );
}
